// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import * as ChannelCommands from '@app/features/channel/commands/ChannelCommands';
import type {Channel} from '@app/features/channel/models/Channel';
import Users from '@app/features/user/state/Users';
import {makeAutoObservable} from 'mobx';

export interface ChannelAnnouncement {
	channelId: string;
	text: string;
	authorTag?: string;
	updatedAt: number;
	pinned: boolean;
}

const STORAGE_COLLAPSED_PREFIX = 'fluxer:announcement_collapsed:';
const STORAGE_ANNOUNCEMENT_PREFIX = 'fluxer:announcement:';

export function getCleanChannelTopic(rawTopic: string | null | undefined): string | null {
	if (!rawTopic) return null;
	const match = rawTopic.match(/^\[STICKY(?::[^\]]+)?\]\s*([\s\S]*)$/i);
	if (match) {
		return match[1]?.trim() || '';
	}
	return rawTopic;
}

class ChannelAnnouncementStore {
	private announcements = new Map<string, ChannelAnnouncement | null>();
	private collapsedMap = new Map<string, boolean>();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	getAnnouncement(channel: Channel | null | undefined): ChannelAnnouncement | null {
		if (!channel) return null;
		const channelId = channel.id;
		const rawTopic = channel.topic?.trim();

		// If channel has a topic from the server (synchronized to all users)
		if (rawTopic && rawTopic.length > 0) {
			const stickyMatch = rawTopic.match(/^\[STICKY(?::([^\]]+))?\]\s*([\s\S]*)$/i);
			if (stickyMatch) {
				const authorTag = stickyMatch[1]?.trim() || 'Staff';
				const text = stickyMatch[2]?.trim() || '';
				if (text.length > 0) {
					const announcement: ChannelAnnouncement = {
						channelId,
						text,
						authorTag,
						updatedAt: Date.now(),
						pinned: true,
					};
					this.announcements.set(channelId, announcement);
					return announcement;
				}
			}

			// If channel has a standard topic, synthesize a default pinned announcement
			const fallback: ChannelAnnouncement = {
				channelId,
				text: rawTopic,
				authorTag: 'Channel Topic',
				updatedAt: Date.now(),
				pinned: true,
			};
			this.announcements.set(channelId, fallback);
			return fallback;
		}

		// Fallback: check localStorage cache only if channel.topic is absent
		if (typeof window !== 'undefined') {
			try {
				const item = localStorage.getItem(`${STORAGE_ANNOUNCEMENT_PREFIX}${channelId}`);
				if (item) {
					const saved = JSON.parse(item) as ChannelAnnouncement;
					if (saved && saved.pinned && saved.text) {
						this.announcements.set(channelId, saved);
						return saved;
					}
				}
			} catch {
				// Ignore parse error
			}
		}

		this.announcements.set(channelId, null);
		return null;
	}

	async setAnnouncement(channelId: string, text: string, pinned = true): Promise<void> {
		const authorId = Authentication.currentUserId ?? 'unknown';
		const authorUser = Users.getUser(authorId);
		const authorTag = authorUser?.displayName || authorUser?.tag || authorUser?.username || 'Staff';
		const trimmedText = text.trim();

		const announcement: ChannelAnnouncement = {
			channelId,
			text: trimmedText,
			authorTag,
			updatedAt: Date.now(),
			pinned,
		};

		this.announcements.set(channelId, announcement);

		if (typeof window !== 'undefined') {
			try {
				localStorage.setItem(`${STORAGE_ANNOUNCEMENT_PREFIX}${channelId}`, JSON.stringify(announcement));
			} catch {
				// Ignore storage error
			}
		}

		// Persist to server via channel.topic so ALL users see it across all devices
		const serverTopic = `[STICKY:${authorTag}] ${trimmedText}`;
		await ChannelCommands.update(channelId, {topic: serverTopic});
	}

	async deleteAnnouncement(channelId: string): Promise<void> {
		this.announcements.set(channelId, null);

		if (typeof window !== 'undefined') {
			try {
				localStorage.removeItem(`${STORAGE_ANNOUNCEMENT_PREFIX}${channelId}`);
			} catch {
				// Ignore storage error
			}
		}

		// Persist to server by clearing topic so banner disappears for all users
		await ChannelCommands.update(channelId, {topic: null});
	}

	isCollapsed(channelId: string): boolean {
		if (this.collapsedMap.has(channelId)) {
			return this.collapsedMap.get(channelId)!;
		}

		let collapsed = false;
		if (typeof window !== 'undefined') {
			try {
				collapsed = localStorage.getItem(`${STORAGE_COLLAPSED_PREFIX}${channelId}`) === 'true';
			} catch {
				// Ignore
			}
		}

		this.collapsedMap.set(channelId, collapsed);
		return collapsed;
	}

	toggleCollapsed(channelId: string): void {
		const next = !this.isCollapsed(channelId);
		this.collapsedMap.set(channelId, next);
		if (typeof window !== 'undefined') {
			try {
				localStorage.setItem(`${STORAGE_COLLAPSED_PREFIX}${channelId}`, String(next));
			} catch {
				// Ignore
			}
		}
	}
}

const instance = new ChannelAnnouncementStore();
export default instance;
